<template>
  <div class="account-purchase">
    <h1 class="h3 mb-4">
      Details
    </h1>
    <b-card
      v-if="request && listing"
      class="mb-4">
      <div
        class="account-purchase__purchase-info">
        <b-row>
          <b-col
            :cols="12"
            :md="3"
            :lg="2">
            <listing-image
              class="account-purchases__purchase-image mb-3"
              :src="listing.imageUrl"/>
          </b-col>
          <b-col
            :cols="12"
            :md="9"
            :lg="10">
            <div
              class="account-purchase__info-title">
              <h2 class="h4">
                {{ listing.title }}
              </h2>
              <div v-if="request.status === 'Pending'">
                <div v-if="request.isSeller">
                  <p>
                    You received a Purchase Request for this item
                    on {{ request.createdAt | date }}.
                  </p>
                  <p>
                    Once you confirm this Purchase Request,
                    you'll be able to use ZoopLocal Messages to
                    arrange delivery and payment for the item.
                  </p>
                </div>
                <div v-else>
                  <p>
                    You submitted a Purchase Request for this item
                    on {{ request.createdAt | date }}.
                  </p>
                  <p>
                    Once the seller confirms your Purchase Request,
                    you'll be able to use ZoopLocal Messages to
                    arrange delivery and payment for the item.
                  </p>
                </div>
              </div>
              <div v-if="request.status === 'Confirmed'">
                <div v-if="request.isSeller">
                  <p>
                    You confirmed this Purchase Request on {{ request.updatedAt | date }}.
                  </p>
                  <p>
                    Please contact the buyer by ZoopLocal
                    Message below to confirm delivery and
                    payment. If you have any problems with
                    the transaction, please email
                    support@zooplocal.com within 72 hours.
                  </p>
                </div>
                <div v-else>
                  <p>
                    The seller confirmed this Purchase Request on {{ request.updatedAt | date }}.
                  </p>
                  <p>
                    Please contact the seller by ZoopLocal
                    Message below to confirm delivery and
                    payment. If you have any problems with
                    the transaction, please email
                    support@zooplocal.com within 72 hours.
                  </p>
                </div>
              </div>
              <div v-if="request.status === 'Expired'">
                <p
                  class="font-weight-bold text-danger">
                  <font-awesome-icon
                    :icon="['fas', 'times']"/>
                  This Purchase Request expired {{ request.expiresAt | relativeDate }}.
                </p>
              </div>
              <div v-if="request.status === 'Completed'">
                <p
                  class="font-weight-bold text-success">
                  <font-awesome-icon
                    :icon="['fas', 'check']"/>
                  This purchase was completed {{ request.updatedAt | relativeDate }}.
                </p>
              </div>
              <div>
                <span
                  v-if="request.status === 'Pending' && request.isSeller">
                  <a
                    href="javascript:void(0);"
                    class="mr-3 text-success font-weight-bold"
                    @click="confirmPurchaseRequest(request.id)">
                    Confirm Request&hellip;
                  </a>
                  <a
                    href="javascript:void(0);"
                    class="mr-3 text-danger font-weight-bold"
                    @click="denyPurchaseRequest(request.id)">
                    Deny Request&hellip;
                  </a>
                </span>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <h1 class="h3 mb-4">
      ZoopLocal Messaging
    </h1>
    <b-card v-if="request && request.canViewMessages">
      <b-alert
        v-if="!request.canSendMessages"
        show
        variant="warning">
        This Purchase Request is no longer available.
        You cannot send new messages, but you can read
        any messages that were sent before it expired.
      </b-alert>
      <!-- eslint-disable max-len -->
      <div class="account-purchase__conversation">
        <div
          v-if="messages.length === 0 && request.canSendMessages"
          class="text-center p-3">
          <h3 class="h4">
            You're the first one here!
          </h3>
          <p>
            Use ZoopLocal Messages to coordinate payment
            and delivery of the item with the
            <span v-if="request.isSeller">
              buyer.
            </span>
            <span v-else>
              seller.
            </span>
          </p>
        </div>
        <div
          v-else-if="messages.length === 0 && !request.canSendMessages"
          class="text-center p-3">
          <h3 class="h4">
            No messages were found.
          </h3>
          <p class="mb-0">
            This Purchase Request expired before either
            participant sent any messages.
          </p>
        </div>
        <div
          v-for="message in messages"
          :key="message.id"
          :class="{ 'account-purchase__conversation-entry--sent': message.isSender, 'account-purchase__conversation-entry--received': !message.isSender }"
          class="account-purchase__conversation-entry">
          <div class="account-purchase__conversation-message">
            {{ message.message }}
            <p class="account-purchase__conversation-meta">
              <span v-if="message.isSender">
                Sent by you {{ message.createdAt | relativeDate }}
              </span>
              <span v-else>
                Received from
                <span v-if="request.isSeller">
                  buyer
                </span>
                <span v-else>
                  seller
                </span>
                {{ message.createdAt | relativeDate }}
              </span>
            </p>
          </div>
        </div>
        <div
          v-if="queuedMessage"
          class="account-purchase__conversation-entry--sent account-purchase__conversation-entry account-purchase__conversation-entry--ghost">
          <div class="account-purchase__conversation-message">
            {{ queuedMessage.message }}
            <p class="account-purchase__conversation-meta">
              <span v-if="!queuedMessage.isComplete && !queuedMessage.isError">
                <em>Sending...</em>
              </span>
              <span v-else-if="queuedMessage.isError">
                Failed to send. Please try again.
              </span>
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="request.canSendMessages"
        class="account-purchase__conversation-input">
        <b-input-group>
          <b-textarea
            v-model="messageForm"
            rows="1"
            max-rows="3"
            size="md"
            @keyup.enter.native="sendMessage"
            placeholder="Enter your message"/>
          <b-input-group-append>
            <b-button
              variant="primary"
              @click="sendMessage">
              <span class="d-none d-md-block">
                Send Message
              </span>
              <span class="d-md-none">
                <font-awesome-icon
                  :icon="['fa', 'paper-plane']"/>
              </span>
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <div class="text-right text-muted my-2">
          <small>Press Enter to send, or Shift-Enter to add a line break</small>
        </div>
      </div>
      <!-- eslint-enable max-len-->
    </b-card>
    <b-card v-else-if="request">
      <div class="text-center p-3">
        <h3 class="h4">Messaging Not Available</h3>
        <p class="m-0">
          <span
            v-if="request.isSeller">
            Messaging will be available once you confirm
            this Purchase Request.
          </span>
          <span
            v-else>
            Messaging will be available once this Purchase
            Request is confirmed.
          </span>
        </p>
      </div>
    </b-card>
    <div
      v-if="request.status === 'Confirmed'"
      class="mt-4">
      <b-card>
        <p>
          Once you have completed the sale, please mark this
          item as delivered.
        </p>
        <a
          v-if="request.status === 'Confirmed'"
          href="javascript:void(0);"
          class="font-weight-bold text-success mr-3"
          @click="completePurchaseRequest(request.id)">
          Mark as Delivered&hellip;
        </a>
      </b-card>
    </div>
  </div>
</template>

<script>
import relativeDate from '@/filters/relativeDate.filter'
import date from '@/filters/date.filter'
import ListingImage from '@/components/images/ListingImage.vue'

export default {
  name: 'AccountPurchase',
  components: {
    ListingImage,
  },
  filters: {
    relativeDate,
    date,
  },
  data() {
    return {
      listing: null,
      request: null,
      queuedMessage: null,
      messageInterval: null,
      messageForm: '',
      messages: [],
    }
  },
  methods: {
    scrollConversationWindow() {
      const conversationContainer = this.$el.querySelector('.account-purchase__conversation')
      conversationContainer.scrollTop = conversationContainer.scrollHeight
    },
    async confirmPurchaseRequest(id) {
      const answer = await this.$bvModal.msgBoxConfirm('Are you sure you want to confirm this request?', {
        title: 'Confirm Request',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Yes, confirm this request',
        cancelTitle: 'No, cancel',
        cancelVariant: 'default',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
      })
      if (answer === false) return
      try {
        await this.$http.post(`/request/${id}/confirm`)
      } catch (err) {
        console.log(err)
      }
      this.fetchRequest()
    },
    async completePurchaseRequest(id) {
      const answer = await this.$bvModal.msgBoxConfirm('Are you sure you want to mark this item as delivered?', {
        title: 'Mark Delivered',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Yes, mark delivered',
        cancelTitle: 'No, cancel',
        cancelVariant: 'default',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
      })
      if (answer === false) return
      try {
        await this.$http.post(`/request/${id}/complete`)
      } catch (err) {
        console.log(err)
      }
      this.$router.push('/account/purchases')
    },
    async denyPurchaseRequest(id) {
      const answer = await this.$bvModal.msgBoxConfirm('Are you sure you want to deny this request?', {
        title: 'Deny Request',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes, deny this request',
        cancelTitle: 'No, cancel',
        cancelVariant: 'default',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
      })
      if (answer === false) return
      try {
        await this.$http.post(`/request/${id}/deny`)
      } catch (err) {
        console.log(err)
      }
      this.$router.push('/account/purchases')
    },
    async fetchRequest() {
      try {
        const { data: { request, listing } } = await this.$http.get(`/request/${this.$route.params.id}`)
        this.request = request
        this.listing = listing

        if (this.request.canViewMessages) {
          this.$nextTick(() => {
            this.scrollConversationWindow()
          })
        }
      } catch (err) {
        console.log(err)
      }

      if (this.request.canViewMessages === true) {
        this.fetchMessages(true)
      }

      if (this.request.canSendMessages === true) {
        this.messageInterval = setInterval(() => {
          this.fetchMessages()
        }, 1500)
      }
    },
    async fetchMessages(initial) {
      try {
        let since

        if (initial) {
          since = undefined
        } else {
          since = this.messages.length
            ? this.messages[this.messages.length - 1].createdAt
            : undefined
        }

        const { data: { messages } } = await this.$http.get(`/request/${this.id}/messages`, {
          params: {
            since,
          },
        })

        if (this.queuedMessage && this.queuedMessage.isComplete) {
          this.queuedMessage = null
        }

        // New messages
        if (!initial && messages.length) {
          this.messages = this.messages.concat(messages)
          this.$nextTick(() => {
            this.scrollConversationWindow()
          })
        } else if (initial) {
          this.messages = messages
          this.$nextTick(() => {
            this.scrollConversationWindow()
          })
        }
      } catch (err) {
        console.log(err)
      }
    },
    async sendMessage(evt) {
      if (evt.code === 'Enter' && evt.shiftKey === true) {
        return
      }
      if (!this.messageForm.length) return
      this.queuedMessage = {
        id: this.messages.length,
        isSender: true,
        message: this.messageForm,
        createdAt: Date.now(),
      }
      this.messageForm = ''
      this.$nextTick(() => {
        this.scrollConversationWindow()
      })
      try {
        await this.$http.post('/messages', {
          message: this.queuedMessage.message,
          requestsId: this.id,
        })
        this.queuedMessage.isComplete = true
      } catch (err) {
        this.queuedMessage.isError = true
        console.error(err)
      }
    },
  },
  created() {
    this.fetchRequest()
  },
  beforeRouteLeave(to, from, next) {
    clearInterval(this.messageInterval)
    next()
  },
  computed: {
    id() {
      return this.$route.params.id
    },
  },
}
</script>

<style lang="scss">
.account-purchase__purchase-info {
  display: flex;
  align-items: flex-start;
}

.account-purchase__purchase-info-footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.account-purchase__conversation {
  max-height: 40vh;
  overflow: scroll;
}

.account-purchase__conversation-message {
  white-space: pre-line;
}

.account-purchase__conversation-entry {
  display: flex;

  &--received {
    justify-content: flex-start;
    color: #000;

    .account-purchase__conversation-message {
      background-color: #f7f7f7;
    }
  }

  &--sent {
    justify-content: flex-end;
    color: #fff;

    .account-purchase__conversation-message {
      background-color: #1a6199;
    }
  }

  &--ghost {
    opacity: 0.5;
  }
}

.account-purchase__conversation-message {
  display: block;
  padding: 10px;
  max-width: 75%;
  margin-bottom: 15px;
}

.account-purchase__conversation-meta {
  font-size: 0.8rem;
  margin: 0;
  opacity: 0.8;
  padding: 0;
}

.account-purchase__conversation-input {
  background: #fff;
  padding: 15px;
  border-top: 2px solid #f7f7f7;
}
</style>
